(() => {
  if (window.BSGVideoBanner) {
    window.BSGVideoBanner.initializeAll();
    return;
  }

  const SELECTOR = '[data-bsg-video-banner]';
  const MINIMUM_PLAY_VISIBILITY = 0.2;
  const records = new Map();
  const scrollingContentRecords = new Map();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let scrollingContentFrame = null;

  const updateScrollingContent = () => {
    scrollingContentFrame = null;
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;

    scrollingContentRecords.forEach((record, banner) => {
      if (!banner.isConnected) {
        scrollingContentRecords.delete(banner);
        return;
      }

      let offset = 0;
      if (isDesktop && !reduceMotion.matches) {
        const stageRect = record.stage.getBoundingClientRect();
        const contentStyle = window.getComputedStyle(record.content);
        const paddingTop = parseFloat(contentStyle.paddingTop) || 0;
        const paddingBottom = parseFloat(contentStyle.paddingBottom) || 0;
        const maximumOffset = Math.max(
          0,
          record.stage.clientHeight - record.box.offsetHeight - paddingTop - paddingBottom
        );
        const viewportBottomOffset =
          window.innerHeight -
          paddingBottom -
          stageRect.top -
          paddingTop -
          record.box.offsetHeight;
        offset = Math.min(maximumOffset, Math.max(0, viewportBottomOffset));
      }

      record.box.style.setProperty('--bsg-video-banner-scroll-content-y', `${Math.round(offset)}px`);
    });
  };

  const requestScrollingContentUpdate = () => {
    if (scrollingContentFrame !== null) return;
    scrollingContentFrame = window.requestAnimationFrame(updateScrollingContent);
  };

  const initializeScrollingContent = (banner) => {
    if (banner.dataset.scrollContentDesktop !== 'true' || scrollingContentRecords.has(banner)) return;

    const stage = banner.querySelector('.bsg-video-banner__stage');
    const content = banner.querySelector('.bsg-video-banner__content');
    const box = banner.querySelector('.bsg-video-banner__box');
    if (!stage || !content || !box) return;

    scrollingContentRecords.set(banner, { stage, content, box });
    requestScrollingContentUpdate();
  };

  const safePlay = (video) => {
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  const scheduleAfterInitialLoad = (callback) => {
    const scheduleWhenIdle = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout: 1500 });
      } else {
        window.setTimeout(callback, 250);
      }
    };

    if (document.readyState === 'complete') {
      scheduleWhenIdle();
    } else {
      window.addEventListener('load', scheduleWhenIdle, { once: true });
    }
  };

  const hydrateVideo = (record) => {
    if (record.hydrated) return;

    record.video.classList.remove('is-ready');
    record.video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    record.video.preload = 'metadata';
    record.video.load();
    record.hydrated = true;
  };

  const chooseActiveVideo = () => {
    let activeRecord = null;

    records.forEach((record, banner) => {
      if (!banner.isConnected) {
        records.delete(banner);
        return;
      }

      if (!activeRecord && record.visibility >= MINIMUM_PLAY_VISIBILITY) {
        activeRecord = record;
      } else if (
        activeRecord &&
        record.visibility >= MINIMUM_PLAY_VISIBILITY &&
        record.visibility > activeRecord.visibility
      ) {
        activeRecord = record;
      }
    });

    records.forEach((record) => {
      const shouldPlay =
        record === activeRecord &&
        record.visibility >= MINIMUM_PLAY_VISIBILITY &&
        record.hydrationAllowed &&
        !document.hidden &&
        !reduceMotion.matches;

      if (shouldPlay) {
        hydrateVideo(record);
        safePlay(record.video);
      } else if (!record.video.paused) {
        record.video.pause();
      }
    });
  };

  const preloadObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const record = records.get(entry.target);
            if (record) hydrateVideo(record);
            preloadObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '300px 0px' }
      )
    : null;

  const playbackObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const record = records.get(entry.target);
            if (record) record.visibility = entry.isIntersecting ? entry.intersectionRatio : 0;
          });
          chooseActiveVideo();
        },
        { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
      )
    : null;

  const initializeBanner = (banner) => {
    initializeScrollingContent(banner);
    if (records.has(banner)) return;

    const video = banner.querySelector('[data-bsg-video-banner-video]');
    if (!video) return;

    const record = {
      banner,
      video,
      visibility: 0,
      hydrated: !video.querySelector('source[data-src]'),
      hydrationAllowed: banner.dataset.loadingPriority !== 'quick'
    };
    records.set(banner, record);

    video.addEventListener('canplay', () => video.classList.add('is-ready'));
    video.addEventListener('playing', () => video.classList.add('is-ready'));

    if (banner.dataset.loadingPriority === 'quick') {
      scheduleAfterInitialLoad(() => {
        if (!banner.isConnected) return;
        record.hydrationAllowed = true;
        chooseActiveVideo();
      });
    } else if (preloadObserver) {
      preloadObserver.observe(banner);
    } else {
      hydrateVideo(record);
    }

    if (playbackObserver) {
      playbackObserver.observe(banner);
    } else {
      record.visibility = 1;
      chooseActiveVideo();
    }

    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const handleBreakpointChange = () => {
      if (!record.hydrated) return;
      video.classList.remove('is-ready');
      video.load();
      chooseActiveVideo();
    };

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', handleBreakpointChange);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(handleBreakpointChange);
    }
  };

  const initializeAll = (scope = document) => {
    if (scope.matches && scope.matches(SELECTOR)) initializeBanner(scope);
    scope.querySelectorAll(SELECTOR).forEach(initializeBanner);
  };

  window.BSGVideoBanner = { initializeAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('visibilitychange', chooseActiveVideo);
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  window.addEventListener('scroll', requestScrollingContentUpdate, { passive: true });
  window.addEventListener('resize', requestScrollingContentUpdate);

  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', () => {
      requestScrollingContentUpdate();
      chooseActiveVideo();
    });
  }
})();
